import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { IContact, ImpersonationBroker, asApiError, excludeKeysOf } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export type AddContactVariables = {
	companyId: string;
	contact: IContact;
};

/**
 * Create a new contact for a lead via `POST lead/{companyId}/contact` — the
 * legacy endpoint exposed by `AidaContactViewModel.Create`. The full IContact
 * payload is sent (including `companyId` and `company` if the caller has it),
 * matching what `useSaveContact` shipped on the legacy queue.
 *
 * On success the contacts query is invalidated so the new row appears in the
 * LEFT column.
 */
export function useAddContact(): UseMutationResult<IContact, Error, AddContactVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<IContact, Error, AddContactVariables>({
		mutationFn: async ({ companyId, contact }) => {
			// Strip server-computed fields the contact endpoint rejects on write:
			// `keyFacts` (parity with `EntityViewModel.mUpdate`) and `primaryEmail`
			// (the backend derives the primary from `emailAddresses`; legacy
			// `useSaveContact` never sends it).
			const body = excludeKeysOf(contact, ['keyFacts', 'primaryEmail']);
			const result = await userSession.webServiceHelper.callWebServiceAsync<IContact>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${companyId}/contact`,
					queryParams: { allowDuplicateEmail: true },
				}),
				'POST',
				body
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.contacts(variables.companyId) });
		},
	});
}
